import { useEffect, useState } from "react";

import styles from "@styles/creative-details.module.scss";

import { BoxTypes } from "@boxes/BoxTypes";
import { Button, Message } from "@components";
import { If } from "@components/control-statements";
import { Environment } from "@core/Environment";
import { Currency } from "@utils/Currency";
import classNames from "classnames/bind";
import { CreativeReportExportDrawer } from "./CreativeReportExportDrawer";
const css = classNames.bind(styles);

const BenefitTypes = {
  FREE_DELIVERY: "FREE_DELIVERY",
  CASH_DISCOUNT: "CASH_DISCOUNT",
  PERCENTAGE_DISCOUNT: "PERCENTAGE_DISCOUNT",
  PRODUCT_DISCOUNT: "PRODUCT_DISCOUNT",
  PRODUCT_CASH_DISCOUNT: "PRODUCT_CASH_DISCOUNT",
  CASHBACK: "CASHBACK",
};

const getMessageTypeByBoxType = (boxType) => {
  switch (boxType) {
    case BoxTypes.SmsCreativeAction:
      return "SMS";
    case BoxTypes.PushCreativeAction:
      return "Push";
    case BoxTypes.EmailCreativeAction:
      return "E-mail";
    case BoxTypes.WhatsAppCreativeAction:
      return "WhatsApp";
  }
};

const getTimeInterval = (hour) => {
  const horaries = [];
  for (let i = 0; i <= 22; i++) {
    horaries.push({
      value: `${String(i).padStart(2, "0")}:00`,
      label: `${String(i).padStart(2, "0")}h - ${String(i + 1).padStart(
        2,
        "0"
      )}h`,
    });
  }
  const horary = horaries.find((h) => hour == h.value);
  return horary?.label;
};

const getBenefitValue = (benefit) => {
  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";

  const benefitType = benefit?.type;
  switch (benefitType) {
    case BenefitTypes.PERCENTAGE_DISCOUNT:
    case BenefitTypes.PRODUCT_DISCOUNT:
    case BenefitTypes.CASHBACK:
      return programByPoint
        ? `${benefit?.value || 0} pontos para cada R$100`
        : `${benefit?.value || 0}%`;
    case BenefitTypes.FREE_DELIVERY:
      return "Entrega grátis";
    case BenefitTypes.CASH_DISCOUNT:
    case BenefitTypes.PRODUCT_CASH_DISCOUNT:
    default:
      return Currency.formatCurrency(+(benefit?.value || 0));
  }
};

export default function CreativeDetails({
  flowId,
  flowType,
  creative,
  node,
  report,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [showReportDrawer, setShowReportDrawer] = useState(false);

  return (
    <div className={styles.creative} onClick={() => setCollapsed(!collapsed)}>
      <div className={styles.header}>
        <div className="d-flex justify-between">
          <p className={styles.name}>{creative?.creative?.name}</p>
          <div className={css({ chevron: true, rotate: collapsed })}>
            <i className="fas fa-chevron-down" />
          </div>
        </div>
        <If cond={!collapsed}>
          <div className="d-flex align-center">
            <div className={styles.type}>
              {getMessageTypeByBoxType(node?.data?.type)}
            </div>
            <div className={styles.value}>
              {Currency.formatCurrency(+(report?.conversionTotal || 0))}
            </div>
          </div>
        </If>
      </div>

      <If cond={collapsed}>
        <div className={styles.details}>
          <p className={styles.type}>
            Criativo de{" "}
            <strong>{getMessageTypeByBoxType(node?.data?.type)}</strong>
          </p>
          <div className="d-flex align-center justify-between">
            <div className="d-flex flex-column">
              <span className={styles.salesValue}>Valor em vendas</span>
              <span className={styles.value}>
                {Currency.formatCurrency(+(report?.conversionTotal || 0))}
              </span>
            </div>
          </div>

          <div className={styles.totalizers}>
            <div className={styles.totalizer}>
              <div className={styles.icon}>
                <i className="fal fa-paper-plane-top" />
              </div>
              <span className={styles.value}>{report?.sent || 0}</span>
            </div>
            {creative?.type === BoxTypes.EmailCreativeAction && (
              <div className={styles.totalizer}>
                <div className={styles.icon}>
                  <i className="fal fa-check-double" />
                </div>
                <span className={styles.value}>{report?.opens || 0}</span>
              </div>
            )}
            <div className={styles.totalizer}>
              <div className={styles.icon}>
                <i className="fal fa-arrow-pointer" />
              </div>
              <span className={styles.value}>{report?.clicks || 0}</span>
            </div>
            <div className={styles.totalizer}>
              <div className={styles.icon}>
                <i className="fal fa-sack-dollar" />
              </div>
              <span className={styles.value}>
                {report?.conversionQuantity || 0}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.infos}>
          <div className={styles.info}>
            <p>Momento do disparo</p>
            <If cond={flowType == "EVENT"}>
              <Message visible>
                O criativo será disparado quando o evento selecionado for
                identificado
              </Message>
            </If>
            <If cond={flowType != "EVENT"}>
              <span>{getTimeInterval(creative?.data?.sendTime)}</span>
            </If>
          </div>

          <If cond={creative?.coupon?.code}>
            <div className={styles.info}>
              <p>Código do benefício</p>
              <span>
                {creative?.coupon?.code}
                <If cond={Environment.id !== 51}>
                  &nbsp;&nbsp;-&nbsp;&nbsp;
                  <strong>{creative?.coupon?.pin || ""}</strong>
                </If>
              </span>
            </div>
            <div className={styles.info}>
              <p>Valor do benefício</p>
              <span>{getBenefitValue(creative?.coupon)}</span>
            </div>
            <div className={styles.info}>
              <p>Valor do pedido mínimo</p>
              <span>
                {Currency.formatCurrency(+(creative?.coupon?.min_order || 0))}
              </span>
            </div>
            <If cond={creative?.coupon?.expiration?.gain?.remind?.active}>
              <div className={styles.info}>
                <p>Lembrete de expiração de ganho</p>
                <span>
                  {creative?.coupon?.expiration?.gain?.remind?.days} dias
                </span>
              </div>
            </If>
            {/* <If cond={creative?.coupon?.type == 'CASHBACK' && creative?.coupon?.expiration?.spend?.remind?.active}>
                <div className={styles.info}>
                    <p>Lembrete de expiração de gasto</p>
                    <span>{creative?.coupon?.expiration?.spend?.remind?.days} dias</span>
                </div>
            </If> */}
            <div className={styles.info}>
              <p>URL de acesso</p>
              <span>
                {creative?.url?.type == "STANDARD"
                  ? Environment.defaultShortUrl
                  : creative?.url?.url}
              </span>
            </div>
            <If cond={creative?.coupon?.type == "CASHBACK"}>
              <div className={styles.info}>
                <p>Limite de ganho</p>
                <span>
                  {Currency.formatCurrency(+(creative?.coupon?.max_gain || 0))}
                </span>
              </div>
              {/* <div className={styles.info}>
                    <p>Expiração de gasto</p>
                    <span>{creative?.coupon?.expiration?.spend?.days} dias</span>
                </div> */}
            </If>
            <div className={styles.info}>
              <p>Expiração de ganho</p>
              <span>{creative?.coupon?.expiration?.gain?.days} dias</span>
            </div>
            <If cond={Environment.id == 34}>
              <div className={styles.info}>
                <p>Grupo</p>
                <span>{creative?.coupon?.group || "-"}</span>
              </div>
              <div className={styles.info}>
                <p>Justificativa</p>
                <span>{creative?.coupon?.justification || "-"}</span>
              </div>
            </If>
          </If>
        </div>
      </If>

      <If cond={creative?.type != BoxTypes.EmailCreativeAction}>
        <div className={styles.message}>
          <p>“{creative?.data?.message?.template}”</p>
        </div>
      </If>

      <If cond={collapsed}>
        <div className="mt-20">
          <Button
            text="Outros relatórios"
            className="w-100"
            design="primary"
            onClick={() => setShowReportDrawer(true)}
          />
        </div>
      </If>

      <CreativeReportExportDrawer
        flowId={node?.data?.flow_id}
        visible={showReportDrawer}
        creative={creative?.creative}
        onClose={() => setShowReportDrawer(false)}
      />
    </div>
  );
}
